// UI strings of the startup screen's audio download (shell/AudioGate.tsx): every recording onto the
// device before the app opens (Axel 2026-10-07). Only the state and what the user can do about it
// (no explanation text: docs/FINNISH-STYLE.md, "no filler copy").
import { defineArea } from '../area';

const num = (locale: string) => (n: number, digits: number) => n.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fiNum = num('fi-FI');
const enNum = num('en-GB');

/**
 * Both amounts in one unit: kilobytes under 1 MB (a few new takes), else megabytes, with one
 * decimal while the total is under 10 MB. [done, total, unit index: 0 = kB, 1 = MB]
 */
const amounts = (fmt: (n: number, d: number) => string, done: number, total: number) => {
  const kb = total < 1e6;
  const div = kb ? 1e3 : 1e6;
  const d = kb || total >= 10e6 ? 0 : 1;
  return [fmt(Math.min(done, total) / div, d), fmt(Math.max(total, kb ? 1e3 : 0) / div, d), kb ? 0 : 1] as const;
};

export const audioPreloadFi = {
  loading: 'Ladataan ääniä laitteelle…',
  /** "23 / 55 Mt · 120/598 äänitettä" */
  amount: (done: number, total: number, files: number, filesTotal: number) => {
    const [a, b, u] = amounts(fiNum, done, total);
    return `${a} / ${b} ${u ? 'Mt' : 'kt'} · ${files}/${filesTotal} äänitettä`;
  },
  /** The progress bar's spoken value. */
  amountLabel: (done: number, total: number) => {
    const [a, b, u] = amounts(fiNum, done, total);
    return `${a} / ${b} ${u ? 'megatavua' : 'kilotavua'}`;
  },
  stoppedNetwork: 'Äänien lataus keskeytyi.',
  stoppedOffline: 'Yhteys katkesi.',
  stoppedStorage: 'Laitteella ei ole tilaa kaikille äänille.',
  missing: (n: number) => (n === 1 ? '1 äänite puuttuu vielä.' : `${n} äänitettä puuttuu vielä.`),
  /** Storage: how much is needed and free. */
  room: (need: number, free: number) => `Tarvitaan noin ${fiNum(need / 1e6, 0)} Mt, vapaana ${fiNum(free / 1e6, 0)} Mt.`,
  retry: 'Yritä uudelleen',
  skip: 'Jatka ilman ääniä',
};

export const audioPreloadEn: typeof audioPreloadFi = {
  loading: 'Downloading audio to this device…',
  amount: (done, total, files, filesTotal) => {
    const [a, b, u] = amounts(enNum, done, total);
    return `${a} / ${b} ${u ? 'MB' : 'kB'} · ${files}/${filesTotal} recordings`;
  },
  amountLabel: (done, total) => {
    const [a, b, u] = amounts(enNum, done, total);
    return `${a} / ${b} ${u ? 'megabytes' : 'kilobytes'}`;
  },
  stoppedNetwork: 'The audio download stopped.',
  stoppedOffline: 'The connection was lost.',
  stoppedStorage: 'This device does not have room for all the audio.',
  missing: (n) => (n === 1 ? '1 recording is still missing.' : `${n} recordings are still missing.`),
  room: (need, free) => `About ${enNum(need / 1e6, 0)} MB is needed, ${enNum(free / 1e6, 0)} MB is free.`,
  retry: 'Try again',
  skip: 'Continue without audio',
};

// Messages.audioPreload (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('audioPreload', audioPreloadFi, audioPreloadEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof audioPreloadFi> {}
}
